/**
 * The studio's people, from the person's side.
 *
 * `credits.ts` holds what a game says about the people who made it. This holds what a person
 * says about the games they made, which is the same table read the other way round and a
 * genuinely different page: the game page wants four names, and the studio page wants one name
 * with everything under it.
 *
 * It is also where the admin bodies live, because both directions are written from the same two
 * screens — the team screen, which owns the people, and the game editor, which owns who is on
 * which game.
 */

import { z } from 'zod';
import { slugSchema, timestampSchema, ulidSchema } from './common.js';
import {
  CAREER_START_MIN,
  creditRolesSchema,
  creditRoleIdSchema,
  MAX_CREDITS_PER_GAME,
  teamMemberSchema,
} from './credits.js';
import { gameKindSchema } from './library.js';

/**
 * An optional line of prose, stored trimmed with empty turned into null.
 *
 * The same rule the game fields use, for the same reason: a field somebody cleared and a field
 * nobody filled in are the same thing, and letting both reach the column means every screen
 * drawing it has to test for both.
 */
function nullableText(max: number) {
  return z
    .string()
    .max(max)
    .nullable()
    .optional()
    .transform((value) => {
      const trimmed = (value ?? '').trim();
      return trimmed === '' ? null : trimmed;
    });
}

/* ------------------------------------------------- one game, on a person */

/**
 * A game a person worked on, as their card lists it.
 *
 * It carries the game's address and its artwork because the chip is a link: the whole point of
 * the list is that somebody reading about a level designer can press the game they designed and
 * land on it. Unpublished games are dropped by the API rather than filtered here — a chip that
 * navigates to a 404 would be worse than no chip.
 */
export const memberCreditSchema = z.object({
  gameId: ulidSchema,
  slug: slugSchema,
  title: z.string(),
  kind: gameKindSchema,
  /** The portrait capsule and the small capsule, so a chip can draw whichever it has room for. */
  capsuleImageUrl: z.string().nullable(),
  smallImageUrl: z.string().nullable(),
  roles: z.array(creditRoleIdSchema),
  contribution: z.string(),
  sortOrder: z.number().int(),
});
export type MemberCredit = z.infer<typeof memberCreditSchema>;

/**
 * One person, with everything they have been on.
 *
 * The studio page's card, whole: `GET /v1/team` answers with these and the page draws them
 * without asking anything else, because a card that fetched its own game list would mean three
 * cards firing three requests to render one section.
 */
export const teamMemberProfileSchema = teamMemberSchema.extend({
  credits: z.array(memberCreditSchema),
});
export type TeamMemberProfile = z.infer<typeof teamMemberProfileSchema>;

export const teamResponseSchema = z.object({
  members: z.array(teamMemberProfileSchema),
});
export type TeamResponse = z.infer<typeof teamResponseSchema>;

/* ------------------------------------------------------------------ admin */

/** The year somebody started in games, checked against the calendar rather than a constant. */
export const careerStartYearSchema = z
  .number()
  .int()
  .min(CAREER_START_MIN)
  .refine((year) => year <= new Date().getUTCFullYear(), {
    message: 'a career cannot have started in the future',
  })
  .nullable()
  .optional();

export const createTeamMemberBodySchema = z.object({
  /** Their address, and the key the app's own bundled portraits are matched on. */
  slug: slugSchema,
  name: z.string().trim().min(1).max(80),
  title: z.string().trim().min(1).max(80),
  bio: z.string().max(1200).default(''),
  careerStartYear: careerStartYearSchema,
  location: nullableText(80),
  sortOrder: z.number().int().default(0),
  /** Somebody can be on the books before they are on the page. */
  published: z.boolean().default(true),
});
export type CreateTeamMemberBody = z.infer<typeof createTeamMemberBodySchema>;

export const updateTeamMemberBodySchema = createTeamMemberBodySchema.partial().extend({
  /**
   * A portrait uploaded through `POST /admin/images`, the way every other picture arrives.
   *
   * Null clears it, which drops the member back to whatever the app ships for their slug, and
   * then to their monogram. The three founding portraits are bundled with the client rather
   * than sitting in a bucket, so the studio page draws complete on a machine that has never
   * reached `files.goodbuild.cc`.
   */
  photoImageId: ulidSchema.nullable().optional(),
});
export type UpdateTeamMemberBody = z.infer<typeof updateTeamMemberBodySchema>;

/**
 * Who is on this game, in full.
 *
 * A replace rather than a set of add and remove calls. The editor holds the whole list in a
 * draft and saves it in one go, so this is one round trip and one audit entry, and two admins
 * cannot end up with a half-applied reorder between them.
 */
export const setGameCreditsBodySchema = z.object({
  credits: z
    .array(
      z.object({
        memberId: ulidSchema,
        roles: creditRolesSchema,
        contribution: z.string().trim().max(600).default(''),
      }),
    )
    .max(MAX_CREDITS_PER_GAME)
    // One person cannot be credited twice on the same game; the roles list is what holds their
    // several jobs. Caught here so the unique index never has to answer for it.
    .refine((rows) => new Set(rows.map((r) => r.memberId)).size === rows.length, {
      message: 'somebody is credited twice on this game',
    }),
});
export type SetGameCreditsBody = z.infer<typeof setGameCreditsBodySchema>;

/** A row of `GET /admin/team`: the person, plus the bookkeeping the public shape drops. */
export const adminTeamMemberSchema = teamMemberProfileSchema.extend({
  published: z.boolean(),
  /** Set when a portrait was uploaded, so the editor can tell one from the bundled fallback. */
  hasUploadedPhoto: z.boolean(),
  updatedAt: timestampSchema,
});
export type AdminTeamMember = z.infer<typeof adminTeamMemberSchema>;
